/**
 * @fileoverview Button component - Atomic UI element
 * 
 * A flexible, accessible button component with variants and sizes.
 * Built with Tailwind CSS and CVA for variant management.
 * 
 * @module ui/shared/atoms/Button
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Button variant definitions using CVA
 */
// cva takes one base and one config; see the note in Input.tsx.
const buttonVariants = cva(
  [
    'ui-btn inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 ease-in-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
    'cursor-pointer',
  ],
  {
    variants: {
      variant: {
        default: 'ui-btn-primary bg-primary-500 text-white',
        destructive: 'ui-btn-dangerous bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
        outline: 'ui-btn-default border-transparent text-white',
        ghost: 'ui-btn-variant-ghost bg-transparent text-gray-700 hover:bg-gray-100 focus-visible:ring-primary-500',
        link: 'ui-btn-link text-primary-600 underline-offset-4 hover:underline focus-visible:ring-primary-500 bg-transparent',
        success: 'ui-btn-primary bg-primary-500 text-white',
        warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus-visible:ring-yellow-500 font-medium',
      },
      size: {
        default: 'h-9 px-4 py-2 min-w-[80px] text-sm',
        sm: 'ui-btn-sm h-8 rounded-md px-3 text-xs min-w-[70px]',
        lg: 'ui-btn-lg h-10 rounded-md px-8 min-w-[120px] text-sm',
        icon: 'ui-btn-icon-only h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

/**
 * Button component props
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render as a different element (for composition)
   */
  asChild?: boolean;
  /**
   * Show loading state
   */
  loading?: boolean;
}

/**
 * Button component
 * 
 * A flexible button component with multiple variants and sizes.
 * Fully accessible with keyboard navigation and ARIA support.
 * 
 * @param {ButtonProps} props - Button props
 * @returns {JSX.Element} Button element
 * 
 * @example
 * <Button variant="primary" size="lg">Click me</Button>
 * 
 * @example
 * <Button variant="outline" loading>Processing...</Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };

